import { useEffect, useRef } from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";
import { useContent, useLang } from "@/context/AppContext";
import { SectionHeader, Reveal } from "@/components/ui-bits";

function CountUp({ value, decimals = 0, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!inView) return undefined;
    if (reduce) {
      ref.current.textContent = `${prefix}${value.toFixed(decimals)}${suffix}`;
      return undefined;
    }
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        if (ref.current) ref.current.textContent = `${prefix}${v.toFixed(decimals)}${suffix}`;
      },
    });
    return () => controls.stop();
  }, [inView, value, decimals, prefix, suffix, reduce]);

  return (
    <span ref={ref}>
      {prefix}0{suffix}
    </span>
  );
}

// By-the-numbers spread — huge serif numerals on paper, hairline dividers.
export default function KpiBento() {
  const { t, L } = useLang();
  const { content } = useContent();
  const kpis = content?.kpis || [];

  if (!kpis.length) return null;

  return (
    <section className="bg-obsidian py-20 lg:py-32" data-testid="kpi-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-hairline pb-8">
          <SectionHeader eyebrow={t("kpiEyebrow")} title={t("kpiTitle")} index={2} />
          <Reveal delay={0.15}>
            <p className="max-w-xs text-sm leading-relaxed text-sand">{t("kpiNote")}</p>
          </Reveal>
        </div>
        <div className="grid border-t-0 sm:grid-cols-2 lg:grid-cols-4">
          {kpis.map((kpi, i) => (
            <Reveal key={kpi.id} delay={(i % 4) * 0.07} className="h-full">
              <div
                data-testid={`kpi-card-${kpi.id}`}
                className="group relative h-full border-b border-r border-hairline bg-obsidian p-8 transition-colors duration-300 first:border-l hover:bg-surface sm:[&:nth-child(2n)]:border-l lg:[&:nth-child(2n)]:border-l-0 lg:[&:nth-child(4n)]:border-r-0 sm:p-9"
              >
                <div className="flex items-start justify-between">
                  <p className="font-mono text-[11px] font-bold tracking-[0.2em] text-sand transition-colors group-hover:text-vermilion">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <span className="h-1.5 w-1.5 bg-hairline transition-colors duration-300 group-hover:bg-vermilion" />
                </div>
                <p
                  className="mt-10 font-display text-[56px] font-medium leading-none tracking-[-0.02em] text-bone"
                  data-testid={`kpi-value-${kpi.id}`}
                >
                  <CountUp value={Number(kpi.value) || 0} decimals={Number(kpi.decimals) || 0} prefix={kpi.prefix || ""} suffix={kpi.suffix || ""} />
                </p>
                <p className="mt-5 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-vermilion">{L(kpi.label)}</p>
                <p className="mt-3 text-[13px] leading-relaxed text-sand">{L(kpi.description)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
